/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  GetInvestorStatisticsResponse,
  Investor,
  InvestorType,
  ListInvestorsResponse,
} from '../models';
import {
    GetInvestorStatisticsResponseFromJSON,
    GetInvestorStatisticsResponseToJSON,
    InvestorFromJSON,
    InvestorToJSON,
    InvestorTypeFromJSON,
    InvestorTypeToJSON,
    ListInvestorsResponseFromJSON,
    ListInvestorsResponseToJSON,
} from '../models';

export interface CreateInvestorRequest {
    habitatId: string;
    investor: Investor;
}

export interface DeleteInvestorRequest {
    habitatId: string;
    investorId: string;
}

export interface GetInvestorRequest {
    habitatId: string;
    investorId: string;
}

export interface GetInvestorJsonBlobRequest {
    habitatId: string;
    investorId: string;
}

export interface GetInvestorStatisticsRequest {
    habitatId: string;
    investorId: string;
}

export interface ListInvestorsRequest {
    habitatId: string;
    ids?: Array<string>;
    externalIds?: Array<string>;
    investorTypes?: Array<InvestorType>;
    pageSize?: number;
    nextPageToken?: string;
}

export interface PutInvestorJsonBlobRequest {
    habitatId: string;
    investorId: string;
    requestBody?: { [key: string]: any; };
}

/**
 * InvestorsApi - interface
 * 
 * @export
 * @interface InvestorsApiInterface
 */
export interface InvestorsApiInterface {
    /**
     * Creates an investor within the specified habitat. An error will be returned if an investor already exists with the same `externalId`.
     * @summary Create investor
     * @param {string} habitatId The habitat identifier.
     * @param {Investor} investor 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    createInvestorRaw(requestParameters: CreateInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Investor>>;

    /**
     * Creates an investor within the specified habitat. An error will be returned if an investor already exists with the same `externalId`.
     * Create investor
     */
    createInvestor(requestParameters: CreateInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Investor>;

    /**
     * Deletes the investor, the investor accounts and compositions. Ballots with an expirationTime in the future:   - are deleted and therefore will not be included in poll tallies. Ballots with an expiration time in the past:   - are anonymised so that they are no longer associated with the investor.   - will not be included in calls to ListBallots.   - are still counted in poll tallies. Warning - this action is not reversible.  
     * @summary Delete investor
     * @param {string} habitatId The habitat identifier.
     * @param {string} investorId The investor identifier.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    deleteInvestorRaw(requestParameters: DeleteInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>>;

    /**
     * Deletes the investor, the investor accounts and compositions. Ballots with an expirationTime in the future:   - are deleted and therefore will not be included in poll tallies. Ballots with an expiration time in the past:   - are anonymised so that they are no longer associated with the investor.   - will not be included in calls to ListBallots.   - are still counted in poll tallies. Warning - this action is not reversible.  
     * Delete investor
     */
    deleteInvestor(requestParameters: DeleteInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void>;

    /**
     * Returns the requested investor.
     * @summary Get investor
     * @param {string} habitatId The habitat identifier.
     * @param {string} investorId The investor identifier.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    getInvestorRaw(requestParameters: GetInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Investor>>;

    /**
     * Returns the requested investor.
     * Get investor
     */
    getInvestor(requestParameters: GetInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Investor>;

    /**
     * Returns the schemaless investors JSON blob. 
     * @summary Get an investor JSON blob
     * @param {string} habitatId The habitat identifier.
     * @param {string} investorId The investor identifier.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    getInvestorJsonBlobRaw(requestParameters: GetInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ [key: string]: any; }>>;

    /**
     * Returns the schemaless investors JSON blob. 
     * Get an investor JSON blob
     */
    getInvestorJsonBlob(requestParameters: GetInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ [key: string]: any; }>;

    /**
     * Returns statistics for an investor 
     * @summary Get investor statistics
     * @param {string} habitatId The habitat identifier.
     * @param {string} investorId The investor identifier.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    getInvestorStatisticsRaw(requestParameters: GetInvestorStatisticsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<GetInvestorStatisticsResponse>>;

    /**
     * Returns statistics for an investor 
     * Get investor statistics
     */
    getInvestorStatistics(requestParameters: GetInvestorStatisticsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<GetInvestorStatisticsResponse>;

    /**
     * Returns a list of all the investors in the habitat.  Additionally a list of either Tumelo or external identifiers can be provided to filter the results.  Either one of these two query parameters can be provided, but not both in the same request: - `ids` - `externalIds`  Requests that include both query parameters return a 400 Bad Request response code.  Here is an example of the format for each query parameter:      /habitats/<habitatId>/investors?ids=<value1>,<value2>      /habitats/<habitatId>/investors?externalIds=<value1>,<value2>  The request returns only investors for which a match can be found. Any duplicate investors are returned just once. The order of investors returned is not determined by the `ids` and `externalIds` filters.  The request is paginated if there are more investors to list than the `pageSize`. When passing a nextPageToken, the same filter query parameters must be set for each page request. 
     * @summary List investors
     * @param {string} habitatId The habitat identifier.
     * @param {Array<string>} [ids] A comma separated list of Tumelo investor identifiers.
     * @param {Array<string>} [externalIds] A comma separated list of external investor identifiers.
     * @param {Array<InvestorType>} [investorTypes] An optional list of &#x60;investorTypes&#x60; parameters can be provided, regardless of the existence of other query parameters. If no investor type is provided a default of Retail will be used.  Here is an example of the format for each query parameter using Investor Types:  /habitats/&lt;habitatId&gt;/investors?investorTypes&#x3D;&lt;value1&gt;,&lt;value2&gt;  /habitats/&lt;habitatId&gt;/investors?ids&#x3D;&lt;value1&gt;,&lt;value2&gt;&amp;investorTypes&#x3D;&lt;value1&gt;,&lt;value2&gt;  /habitats/&lt;habitatId&gt;/investors?externalIds&#x3D;&lt;value1&gt;,&lt;value2&gt;&amp;investorTypes&#x3D;&lt;value1&gt;,&lt;value2&gt; 
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    listInvestorsRaw(requestParameters: ListInvestorsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListInvestorsResponse>>;

    /**
     * Returns a list of all the investors in the habitat.  Additionally a list of either Tumelo or external identifiers can be provided to filter the results.  Either one of these two query parameters can be provided, but not both in the same request: - `ids` - `externalIds`  Requests that include both query parameters return a 400 Bad Request response code.  Here is an example of the format for each query parameter:      /habitats/<habitatId>/investors?ids=<value1>,<value2>      /habitats/<habitatId>/investors?externalIds=<value1>,<value2>  The request returns only investors for which a match can be found. Any duplicate investors are returned just once. The order of investors returned is not determined by the `ids` and `externalIds` filters.  The request is paginated if there are more investors to list than the `pageSize`. When passing a nextPageToken, the same filter query parameters must be set for each page request. 
     * List investors
     */
    listInvestors(requestParameters: ListInvestorsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListInvestorsResponse>;

    /**
     * Stores an investor JSON blob at the endpoint. 
     * @summary Put an investor JSON blob
     * @param {string} habitatId The habitat identifier.
     * @param {string} investorId The investor identifier.
     * @param {{ [key: string]: any; }} [requestBody] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof InvestorsApiInterface
     */
    putInvestorJsonBlobRaw(requestParameters: PutInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ [key: string]: any; }>>;

    /**
     * Stores an investor JSON blob at the endpoint. 
     * Put an investor JSON blob
     */
    putInvestorJsonBlob(requestParameters: PutInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ [key: string]: any; }>;

}

/**
 * 
 */
export class InvestorsApi extends runtime.BaseAPI implements InvestorsApiInterface {

    /**
     * Creates an investor within the specified habitat. An error will be returned if an investor already exists with the same `externalId`.
     * Create investor
     */
    async createInvestorRaw(requestParameters: CreateInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Investor>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling createInvestor.');
        }

        if (requestParameters.investor === null || requestParameters.investor === undefined) {
            throw new runtime.RequiredError('investor','Required parameter requestParameters.investor was null or undefined when calling createInvestor.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/habitats/{habitatId}/investors`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestorToJSON(requestParameters.investor),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorFromJSON(jsonValue));
    }

    /**
     * Creates an investor within the specified habitat. An error will be returned if an investor already exists with the same `externalId`.
     * Create investor
     */
    async createInvestor(requestParameters: CreateInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Investor> {
        const response = await this.createInvestorRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Deletes the investor, the investor accounts and compositions. Ballots with an expirationTime in the future:   - are deleted and therefore will not be included in poll tallies. Ballots with an expiration time in the past:   - are anonymised so that they are no longer associated with the investor.   - will not be included in calls to ListBallots.   - are still counted in poll tallies. Warning - this action is not reversible.  
     * Delete investor
     */
    async deleteInvestorRaw(requestParameters: DeleteInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling deleteInvestor.');
        }

        if (requestParameters.investorId === null || requestParameters.investorId === undefined) {
            throw new runtime.RequiredError('investorId','Required parameter requestParameters.investorId was null or undefined when calling deleteInvestor.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/habitats/{habitatId}/investors/{investorId}`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))).replace(`{${"investorId"}}`, encodeURIComponent(String(requestParameters.investorId))),
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Deletes the investor, the investor accounts and compositions. Ballots with an expirationTime in the future:   - are deleted and therefore will not be included in poll tallies. Ballots with an expiration time in the past:   - are anonymised so that they are no longer associated with the investor.   - will not be included in calls to ListBallots.   - are still counted in poll tallies. Warning - this action is not reversible.  
     * Delete investor
     */
    async deleteInvestor(requestParameters: DeleteInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.deleteInvestorRaw(requestParameters, initOverrides);
    }

    /**
     * Returns the requested investor.
     * Get investor
     */
    async getInvestorRaw(requestParameters: GetInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Investor>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling getInvestor.');
        }

        if (requestParameters.investorId === null || requestParameters.investorId === undefined) {
            throw new runtime.RequiredError('investorId','Required parameter requestParameters.investorId was null or undefined when calling getInvestor.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/habitats/{habitatId}/investors/{investorId}`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))).replace(`{${"investorId"}}`, encodeURIComponent(String(requestParameters.investorId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorFromJSON(jsonValue));
    }

    /**
     * Returns the requested investor.
     * Get investor
     */
    async getInvestor(requestParameters: GetInvestorRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Investor> {
        const response = await this.getInvestorRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the schemaless investors JSON blob. 
     * Get an investor JSON blob
     */
    async getInvestorJsonBlobRaw(requestParameters: GetInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ [key: string]: any; }>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling getInvestorJsonBlob.');
        }

        if (requestParameters.investorId === null || requestParameters.investorId === undefined) {
            throw new runtime.RequiredError('investorId','Required parameter requestParameters.investorId was null or undefined when calling getInvestorJsonBlob.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/habitats/{habitatId}/investors/{investorId}/investorJsonBlob`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))).replace(`{${"investorId"}}`, encodeURIComponent(String(requestParameters.investorId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Returns the schemaless investors JSON blob. 
     * Get an investor JSON blob
     */
    async getInvestorJsonBlob(requestParameters: GetInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ [key: string]: any; }> {
        const response = await this.getInvestorJsonBlobRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns statistics for an investor 
     * Get investor statistics
     */
    async getInvestorStatisticsRaw(requestParameters: GetInvestorStatisticsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<GetInvestorStatisticsResponse>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling getInvestorStatistics.');
        }

        if (requestParameters.investorId === null || requestParameters.investorId === undefined) {
            throw new runtime.RequiredError('investorId','Required parameter requestParameters.investorId was null or undefined when calling getInvestorStatistics.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/habitats/{habitatId}/investors/{investorId}/statistics`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))).replace(`{${"investorId"}}`, encodeURIComponent(String(requestParameters.investorId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => GetInvestorStatisticsResponseFromJSON(jsonValue));
    }

    /**
     * Returns statistics for an investor 
     * Get investor statistics
     */
    async getInvestorStatistics(requestParameters: GetInvestorStatisticsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<GetInvestorStatisticsResponse> {
        const response = await this.getInvestorStatisticsRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a list of all the investors in the habitat.  Additionally a list of either Tumelo or external identifiers can be provided to filter the results.  Either one of these two query parameters can be provided, but not both in the same request: - `ids` - `externalIds`  Requests that include both query parameters return a 400 Bad Request response code.  Here is an example of the format for each query parameter:      /habitats/<habitatId>/investors?ids=<value1>,<value2>      /habitats/<habitatId>/investors?externalIds=<value1>,<value2>  The request returns only investors for which a match can be found. Any duplicate investors are returned just once. The order of investors returned is not determined by the `ids` and `externalIds` filters.  The request is paginated if there are more investors to list than the `pageSize`. When passing a nextPageToken, the same filter query parameters must be set for each page request. 
     * List investors
     */
    async listInvestorsRaw(requestParameters: ListInvestorsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListInvestorsResponse>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling listInvestors.');
        }

        const queryParameters: any = {};

        if (requestParameters.ids) {
            queryParameters['ids'] = requestParameters.ids.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.externalIds) {
            queryParameters['externalIds'] = requestParameters.externalIds.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.investorTypes) {
            queryParameters['investorTypes'] = requestParameters.investorTypes.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/habitats/{habitatId}/investors`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListInvestorsResponseFromJSON(jsonValue));
    }

    /**
     * Returns a list of all the investors in the habitat.  Additionally a list of either Tumelo or external identifiers can be provided to filter the results.  Either one of these two query parameters can be provided, but not both in the same request: - `ids` - `externalIds`  Requests that include both query parameters return a 400 Bad Request response code.  Here is an example of the format for each query parameter:      /habitats/<habitatId>/investors?ids=<value1>,<value2>      /habitats/<habitatId>/investors?externalIds=<value1>,<value2>  The request returns only investors for which a match can be found. Any duplicate investors are returned just once. The order of investors returned is not determined by the `ids` and `externalIds` filters.  The request is paginated if there are more investors to list than the `pageSize`. When passing a nextPageToken, the same filter query parameters must be set for each page request. 
     * List investors
     */
    async listInvestors(requestParameters: ListInvestorsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListInvestorsResponse> {
        const response = await this.listInvestorsRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Stores an investor JSON blob at the endpoint. 
     * Put an investor JSON blob
     */
    async putInvestorJsonBlobRaw(requestParameters: PutInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ [key: string]: any; }>> {
        if (requestParameters.habitatId === null || requestParameters.habitatId === undefined) {
            throw new runtime.RequiredError('habitatId','Required parameter requestParameters.habitatId was null or undefined when calling putInvestorJsonBlob.');
        }

        if (requestParameters.investorId === null || requestParameters.investorId === undefined) {
            throw new runtime.RequiredError('investorId','Required parameter requestParameters.investorId was null or undefined when calling putInvestorJsonBlob.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/habitats/{habitatId}/investors/{investorId}/investorJsonBlob`.replace(`{${"habitatId"}}`, encodeURIComponent(String(requestParameters.habitatId))).replace(`{${"investorId"}}`, encodeURIComponent(String(requestParameters.investorId))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body: requestParameters.requestBody,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Stores an investor JSON blob at the endpoint. 
     * Put an investor JSON blob
     */
    async putInvestorJsonBlob(requestParameters: PutInvestorJsonBlobRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ [key: string]: any; }> {
        const response = await this.putInvestorJsonBlobRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
